import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';

const STORAGE_KEY = 'cmi_pms_install_modal_dismissed_v1';

function isRunningStandalone() {
  // iOS Safari uses navigator.standalone; other browsers use display-mode media query.
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator?.standalone === true
  );
}

export default function InstallPwaModal() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(() => isRunningStandalone());
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const isOpen = useMemo(() => {
    // Show only when install is possible and the app isn't already installed.
    return Boolean(deferredPrompt) && !isInstalled && !dismissed;
  }, [deferredPrompt, isInstalled, dismissed]);

  useEffect(() => {
    // Accessibility requirement for react-modal.
    Modal.setAppElement('#root');
  }, []);

  useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing and save event for later trigger.
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const onAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const onDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // ignore
    }
  };

  const onInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome !== 'accepted') onDismiss();
    } finally {
      setDeferredPrompt(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onDismiss}
      contentLabel="Install CMI PMS"
      style={{
        overlay: { backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 9999 },
        content: {
          maxWidth: 380,
          margin: 'auto',
          borderRadius: 12,
          padding: 16,
          inset: 16,
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        },
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Install CMI PMS</div>
          <div style={{ color: '#555', lineHeight: 1.35, fontSize: 13 }}>
            Install the app for a faster experience and easier access from your home screen/desktop.
          </div>
        </div>
        <button
          onClick={onDismiss}
          style={{
            border: 'none',
            background: 'transparent',
            fontSize: 20,
            lineHeight: 1,
            cursor: 'pointer',
          }}
          aria-label="Close"
          title="Close"
        >
          ×
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
        <button
          onClick={onDismiss}
          style={{
            padding: '8px 12px',
            borderRadius: 10,
            border: '1px solid #ddd',
            background: '#fff',
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          Not now
        </button>
        <button
          onClick={onInstall}
          style={{
            padding: '8px 12px',
            borderRadius: 10,
            border: '1px solid #0d6efd',
            background: '#0d6efd',
            color: '#fff',
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          Install
        </button>
      </div>
    </Modal>
  );
}

